"use client";

import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Volume2, VolumeX } from "lucide-react";
import TextGenerateEffect from "@/components/TextGenerateEffect";

const COLS = [
  ["1", "2", "3", "4"],
  ["5", "6", "7", "8"],
  ["9", "10", "11", "12"],
  ["13", "14", "15", "16"],
];

const WAABI_IMAGES = [
  "https://for-media.b-cdn.net/ozgurmd/burak-catan.png",
  "https://for-media.b-cdn.net/ozgurmd/burak-ozgur-pic.JPG",
  "https://for-media.b-cdn.net/ozgurmd/CATAN%20Americas%202026_winner_sm.png",
  "https://for-media.b-cdn.net/ozgurmd/ozgur-catan.jpg",
  "https://for-media.b-cdn.net/ozgurmd/catan-bg.jpg",
  "https://for-media.b-cdn.net/ozgurmd/ozgur-catan2.jpg",
  "https://for-media.b-cdn.net/ozgurmd/burak-hoag.jpg",
  "https://for-media.b-cdn.net/ozgurmd/ozgur-catan.jpg",
  "https://for-media.b-cdn.net/ozgurmd/ozgur-catan5.jpg",
  "https://for-media.b-cdn.net/ozgurmd/ozgur-catan6.jpg",
  "https://for-media.b-cdn.net/ozgurmd/ozgur-catan7.jpg",
  "https://for-media.b-cdn.net/ozgurmd/ozgur-catan8.jpg",
  "https://for-media.b-cdn.net/ozgurmd/ozgur-catan4.jpg",
  "https://for-media.b-cdn.net/ozgurmd/ozgur-catan3.jpg",
  "https://for-media.b-cdn.net/ozgurmd/CATAN%20Americas%202026_finalists.jpg",
  "https://for-media.b-cdn.net/ozgurmd/burak-surfing.jpg",
];

export default function WaabiScrollSection() {
  const sectionRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [origin, setOrigin] = useState("");
  const waabiIframeRef = useRef(null);

  useEffect(() => {
    if (typeof window !== "undefined") setOrigin(window.location.origin);
  }, []);

  // Mute/unmute via the YT IFrame API. Sending postMessage before
  // YouTube's internal widgetapi has finished wiring up throws
  // `isExternalMethodAvailable is not a function` inside YouTube's
  // code — so the on-load sync is deferred 800ms to give it time.
  // User-click sync (mute toggle) fires immediately because by the
  // time someone can tap the button, YouTube is already listening.
  useEffect(() => {
    const iframe = waabiIframeRef.current;
    if (!iframe) return;

    const sendCommand = (func) => {
      if (!iframe.contentWindow) return;
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "command", func, args: [] }),
        "*"
      );
    };

    const syncState = () => {
      sendCommand(muted ? "mute" : "unMute");
      sendCommand("playVideo");
    };

    syncState();
    const onLoad = () => setTimeout(syncState, 800);
    iframe.addEventListener("load", onLoad);
    return () => iframe.removeEventListener("load", onLoad);
  }, [muted]);

  // First user gesture unlocks playback on iOS — kick the iframe then.
  useEffect(() => {
    let fired = false;
    const kick = () => {
      if (fired) return;
      fired = true;
      const iframe = waabiIframeRef.current;
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: "command", func: "playVideo", args: [] }),
          "*"
        );
      }
    };
    const opts = { once: true, passive: true };
    window.addEventListener("touchstart", kick, opts);
    window.addEventListener("click", kick, opts);
    window.addEventListener("scroll", kick, opts);
    return () => {
      window.removeEventListener("touchstart", kick);
      window.removeEventListener("click", kick);
      window.removeEventListener("scroll", kick);
    };
  }, []);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const section = sectionRef.current;
    if (!section) return;

    const heroCopyWords = section.querySelectorAll(".wa-hero-copy .wa-word");
    const heroCopy = section.querySelector(".wa-hero-copy h3");

    gsap.set(heroCopyWords, { opacity: 0 });

    const ctx = gsap.context(() => {
      let isHeroCopyHidden = false;

      ScrollTrigger.create({
        trigger: ".wa-hero",
        start: "top top",
        end: () => `+=${window.innerHeight * 3.5}px`,
        pin: true,
        pinSpacing: false,
        scrub: 1,
        invalidateOnRefresh: true,
        anticipatePin: 1,
        onUpdate: (self) => {
          const progress = self.progress;

          const heroHeaderProgress = Math.min(progress / 0.29, 1);
          gsap.set(".wa-hero-header", { yPercent: -heroHeaderProgress * 100 });

          const heroWordsProgress = Math.max(0, Math.min((progress - 0.29) / 0.21, 1));
          const totalWords = heroCopyWords.length || 1;
          heroCopyWords.forEach((word, i) => {
            const wordStart = i / totalWords;
            const wordEnd = (i + 1) / totalWords;
            const wordOpacity = Math.max(
              0,
              Math.min((heroWordsProgress - wordStart) / (wordEnd - wordStart), 1)
            );
            gsap.set(word, { opacity: wordOpacity });
          });

          if (progress > 0.64 && !isHeroCopyHidden) {
            isHeroCopyHidden = true;
            gsap.to(heroCopy, { opacity: 0, duration: 0.2 });
          } else if (progress <= 0.64 && isHeroCopyHidden) {
            isHeroCopyHidden = false;
            gsap.to(heroCopy, { opacity: 1, duration: 0.2 });
          }

          const heroImgProgress = Math.max(0, Math.min((progress - 0.71) / 0.29, 1));
          const heroImgWidth = gsap.utils.interpolate(window.innerWidth, 150, heroImgProgress);
          const heroImgHeight = gsap.utils.interpolate(window.innerHeight, 150, heroImgProgress);
          const heroImgBorderRadius = gsap.utils.interpolate(0, 10, heroImgProgress);
          gsap.set(".wa-hero-img", {
            width: heroImgWidth,
            height: heroImgHeight,
            borderRadius: heroImgBorderRadius,
          });
        },
      });

      const aboutImgCols = [
        { id: "#wa-about-imgs-col-1", y: -500 },
        { id: "#wa-about-imgs-col-2", y: -250 },
        { id: "#wa-about-imgs-col-3", y: -250 },
        { id: "#wa-about-imgs-col-4", y: -500 },
      ];

      aboutImgCols.forEach(({ id, y }) => {
        gsap.to(id, {
          y,
          scrollTrigger: {
            trigger: ".wa-about",
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        });
      });
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="journey" className="wa-wrap">
      <section className="wa-hero">
        <div className="wa-hero-img">
          <iframe
            ref={waabiIframeRef}
            src={`https://www.youtube.com/embed/pvoOmSrMZxk?autoplay=1&mute=1&controls=0&modestbranding=1&loop=1&playlist=pvoOmSrMZxk&playsinline=1&enablejsapi=1&rel=0${origin ? `&origin=${encodeURIComponent(origin)}` : ""}`}
            title="Waabi hero video"
            className="absolute left-1/2 top-1/2 h-[56.25vw] min-h-full w-[177.77vh] min-w-full -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            allow="autoplay; encrypted-media; picture-in-picture"
          />
        </div>
        <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
          <button
            type="button"
            onClick={() => setMuted((m) => !m)}
            aria-label={muted ? "Unmute video" : "Mute video"}
            className="pointer-events-auto inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-black/35 text-white backdrop-blur transition hover:bg-black/50"
          >
            {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
          </button>
        </div>
        <div className="wa-hero-header">
          <h2>Dr. Ozgur’s influence reaches beyond the operating room. </h2>
        </div>
        <div className="wa-hero-copy">
          <h3>
            {"He is a compelling speaker and on-camera expert who makes complex medical topics accessible to wide audiences."
              .split(" ")
              .map((word, idx) => (
                <span key={`${word}-${idx}`} className="wa-word">
                  {word}{" "}
                </span>
              ))}
          </h3>
        </div>
      </section>

      <section className="wa-about">
        <div className="wa-about-images">
          {COLS.map((col, colIndex) => (
            <div
              key={`col-${colIndex + 1}`}
              className="wa-about-imgs-col"
              id={`wa-about-imgs-col-${colIndex + 1}`}
            >
              {col.map((num, itemIndex) => {
                const flatIndex = colIndex * 4 + itemIndex;
                const src = WAABI_IMAGES[flatIndex];
                return (
                  <div key={num} className="wa-img">
                    <img src={src} alt={`Waabi image ${flatIndex + 1}`} />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="wa-about-header">
          <TextGenerateEffect
            as="h3"
            words="Burak is the **current 2026 Catan Americas Championship & 2025 Catan US Nationals Champion**, a title that reflects his ability to **outmaneuver and outthink the most elite minds** in high-stakes strategy."
            duration={0.6}
            staggerDelay={0.04}
          />
        </div>
      </section>

      <section className="wa-about wa-about--secondary">
        <div className="wa-about-header">
          <TextGenerateEffect
            as="h3"
            words={`This channel is an exploration of the **"Super-Human" output required to lead at this level**. Whether he is performing **complex spinal reconstruction**, dismantling opponents on the global stage, or maintaining peak physical agility, Burak's life is a **masterclass in mental stamina, physical discipline, and the relentless pursuit of perfection**.`}
            duration={0.6}
            staggerDelay={0.04}
          />
        </div>
      </section>
    </section>
  );
}
